import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { ModalTitle } from '../../../global/Styles/titles';
import { useApiClient, validateSuccess2xx } from '../../../../clients/apiAuth';
import { CustomToast, ErrorToast, OkToast } from '../../Toast';
import { moneyFormat } from '../../../../utils/format';

const inputBase =
  'w-full rounded border px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-blue-500 bg-white';
const inputError = 'border-red-500';
const inputOk = 'border-gray-300';

const formatNumberWithDecimals = (value, decimals = 3) => {
  if (value === '' || value === null || value === undefined) return '';
  const num = Number(value);
  if (Number.isNaN(num)) return '';
  return new Intl.NumberFormat('es-CO', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
};

const parseNumberFromInput = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return '';
  let value = String(raw).trim();
  if (!value) return '';
  value = value.replace(/[^\d.,-]/g, '');
  if (!value) return '';
  if (value.includes(',')) {
    value = value.replace(/\./g, '').replace(',', '.');
  } else {
    value = value.replace(/\./g, '');
  }
  const num = Number(value);
  return Number.isNaN(num) ? '' : num;
};

const formatValue = (value, fallback = '-') => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  return String(value);
};

const getRutaLabel = (solicitud) => {
  const origen = solicitud?.ORIGEN || '';
  const destino = solicitud?.DESTINO || '';
  return origen && destino ? `${origen} -> ${destino}` : '-';
};

const getContratoLabel = (numero, id) => {
  if (numero !== null && numero !== undefined && numero !== '') {
    return String(numero);
  }
  if (id !== null && id !== undefined && id !== '') {
    return String(id);
  }
  return '-';
};

const getReporteLabel = (reporte) => {
  if (reporte !== null && reporte !== undefined && reporte !== '') {
    return String(reporte);
  }
  return '-';
};

const ModalTarifasServicio = ({ isOpen, onClose, vehiculo, solicitud, reporte }) => {
  const { get, post, DELETE } = useApiClient();
  const [detalle, setDetalle] = useState([]);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [tarifaOptions, setTarifaOptions] = useState([]);
  const [loadingTarifas, setLoadingTarifas] = useState(false);
  const [form, setForm] = useState({
    TARIFA_ID: '',
    SOLICITUD_DETALLE_CANTIDAD: '',
    SOLICITUD_DETALLE_VALOR: '',
    SOLICITUD_DETALLE_TOTAL: '',
  });
  const [errors, setErrors] = useState({});
  const [canEditValor, setCanEditValor] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const solicitudVehiculoTipoId = vehiculo?.SOLICITUD_VEHICULO_TIPO_ID ?? '';
  const vehiculoTipoId = vehiculo?.VEHICULO_TIPO_ID ?? '';
  const vehiculoTipoDesc = vehiculo?.VEHICULO_TIPO_DESCRIPCION || '-';
  const contratoId = solicitud?.CONTRATO_ID ?? '';
  const contratoNumero = solicitud?.CONTRATO_NUMERO ?? '';

  const totals = useMemo(() => {
    const sum = detalle.reduce((acc, item) => acc + (Number(item.SOLICITUD_DETALLE_TOTAL) || 0), 0);
    return sum;
  }, [detalle]);

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setForm({
      TARIFA_ID: '',
      SOLICITUD_DETALLE_CANTIDAD: '',
      SOLICITUD_DETALLE_VALOR: '',
      SOLICITUD_DETALLE_TOTAL: '',
    });
    setCanEditValor(false);
  }, [isOpen, vehiculoTipoId]);

  useEffect(() => {
    if (!isOpen || !solicitudVehiculoTipoId) return;
    loadDetalle();
  }, [isOpen, solicitudVehiculoTipoId]);

  useEffect(() => {
    if (!isOpen || !vehiculoTipoId || !contratoId) return;
    loadTarifaOptions();
  }, [isOpen, vehiculoTipoId, contratoId]);

  const loadDetalle = async () => {
    setLoadingDetalle(true);
    try {
      const result = await get(`/solicitudDetalle?SOLICITUD_VEHICULO_TIPO_ID=${solicitudVehiculoTipoId}`);
      if (!validateSuccess2xx(result)) {
        CustomToast({ result });
        setDetalle([]);
        return;
      }
      setDetalle(result.data || []);
    } catch (error) {
      ErrorToast('Error:', error);
    } finally {
      setLoadingDetalle(false);
    }
  };

  const loadTarifaOptions = async () => {
    if (!vehiculoTipoId || !contratoId) {
      setTarifaOptions([]);
      setLoadingTarifas(false);
      return;
    }
    try {
      setLoadingTarifas(true);
      const result = await get(`/tarifa?VEHICULO_TIPO_ID=${vehiculoTipoId}&CONTRATO_ID=${contratoId}`);
      if (!validateSuccess2xx(result)) {
        CustomToast({ result });
        setTarifaOptions([]);
        return;
      }
      const lst = (result.data || []).map((row) => ({
        label: `${row.TARIFA_ITEM ?? ''} ${row.TARIFA_NOMBRE ?? ''} ${row.TARIFA_UNIDAD ?? ''} ${moneyFormat(row.TARIFA_VALOR)}`
          .replace(/\s+/g, ' ')
          .trim(),
        item: row.TARIFA_ITEM,
        nombre: row.TARIFA_NOMBRE,
        unidad: row.TARIFA_UNIDAD,
        valor: row.TARIFA_VALOR,
        valorFormatted: moneyFormat(row.TARIFA_VALOR),
        value: row.TARIFA_ID,
        price: row.TARIFA_VALOR,
      }));
      setTarifaOptions(lst);
    } catch (error) {
      ErrorToast('Error:', error);
    } finally {
      setLoadingTarifas(false);
    }
  };

  const handleTarifaChange = (option) => {
    const tarifaId = option?.value || '';
    const selected = tarifaOptions.find((opt) => String(opt.value) === String(tarifaId));
    const price = selected ? Number(selected.price) || 0 : '';
    const cantidad = Number(form.SOLICITUD_DETALLE_CANTIDAD) || 0;
    setForm((prev) => ({
      ...prev,
      TARIFA_ID: tarifaId,
      SOLICITUD_DETALLE_VALOR: price,
      SOLICITUD_DETALLE_TOTAL: cantidad && price ? cantidad * price : '',
    }));
    setCanEditValor(Number(price) === 0);
  };

  const handleCantidadChange = (event) => {
    const cantidad = event.target.value;
    const valor = Number(form.SOLICITUD_DETALLE_VALOR) || 0;
    setForm((prev) => ({
      ...prev,
      SOLICITUD_DETALLE_CANTIDAD: cantidad,
      SOLICITUD_DETALLE_TOTAL: cantidad && valor ? Number(cantidad) * valor : '',
    }));
  };

  const handleValorChange = (event) => {
    const valor = parseNumberFromInput(event.target.value);
    const cantidad = Number(form.SOLICITUD_DETALLE_CANTIDAD) || 0;
    setForm((prev) => ({
      ...prev,
      SOLICITUD_DETALLE_VALOR: valor,
      SOLICITUD_DETALLE_TOTAL: cantidad && valor !== '' ? Number(cantidad) * Number(valor) : '',
    }));
  };

  const formatTarifaOption = (option, { context }) => {
    if (context === 'value') {
      return option.label;
    }
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '60px minmax(0, 1fr) 72px 120px', gap: '8px' }}>
        <span style={{ fontWeight: 600 }}>{option.item}</span>
        <span style={{ whiteSpace: 'normal' }}>{option.nombre}</span>
        <span style={{ textTransform: 'uppercase' }}>{option.unidad}</span>
        <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{option.valorFormatted}</span>
      </div>
    );
  };

  const formattedValor = formatNumberWithDecimals(form.SOLICITUD_DETALLE_VALOR);
  const formattedTotal = formatNumberWithDecimals(form.SOLICITUD_DETALLE_TOTAL);

  const validate = () => {
    const nextErrors = {};
    if (!form.TARIFA_ID) nextErrors.TARIFA_ID = '*';
    if (!form.SOLICITUD_DETALLE_CANTIDAD) nextErrors.SOLICITUD_DETALLE_CANTIDAD = '*';
    if (canEditValor && !form.SOLICITUD_DETALLE_VALOR) nextErrors.SOLICITUD_DETALLE_VALOR = '*';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleAdd = async () => {
    if (!solicitudVehiculoTipoId || !contratoId) {
      ErrorToast('Error: ', 'Contrato o vehículo no disponible.');
      return;
    }
    if (!validate()) {
      ErrorToast('Error: ', 'Complete los campos obligatorios.');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        SOLICITUD_VEHICULO_TIPO_ID: solicitudVehiculoTipoId,
        TARIFA_ID: form.TARIFA_ID,
        SOLICITUD_DETALLE_CANTIDAD: form.SOLICITUD_DETALLE_CANTIDAD,
        SOLICITUD_DETALLE_VALOR: form.SOLICITUD_DETALLE_VALOR || undefined,
      };
      const result = await post(
        '/solicitudDetalle',
        {
          json: JSON.stringify(payload),
        },
        'application/json'
      );
      if (!validateSuccess2xx(result)) {
        CustomToast({ result });
        return;
      }
      OkToast({ message: 'Item agregado!' });
      await loadDetalle();
      setForm((prev) => ({
        ...prev,
        TARIFA_ID: '',
        SOLICITUD_DETALLE_CANTIDAD: '',
        SOLICITUD_DETALLE_VALOR: '',
        SOLICITUD_DETALLE_TOTAL: '',
      }));
      setCanEditValor(false);
    } catch (error) {
      ErrorToast('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!item?.SOLICITUD_DETALLE_ID) return;
    try {
      const result = await DELETE(`/solicitudDetalle/${item.SOLICITUD_DETALLE_ID}`);
      if (!validateSuccess2xx(result)) {
        CustomToast({ result });
        return;
      }
      OkToast({ message: 'Item eliminado!' });
      await loadDetalle();
    } catch (error) {
      ErrorToast('Error:', error);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="min-w-screen animated fadeIn faster outline-none focus:outline-none fixed inset-0 left-0 top-0 z-50 flex h-screen items-center justify-center overflow-hidden bg-cover bg-center bg-no-repeat p-4">
      <div className="fixed inset-0 z-0 bg-black opacity-50"></div>
      <div className="relative mx-auto my-auto w-full max-w-5xl rounded-xl bg-white shadow-lg">
        <ModalTitle>GESTIONAR TARIFAS</ModalTitle>
        <div className="p-6">
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="grid grid-cols-1 gap-3 text-sm laptop:grid-cols-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Placa</p>
                <p className="font-medium text-gray-800">{formatValue(vehiculo?.VEHICULO_PLACA)}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Reporte</p>
                <p className="font-medium text-gray-800">{getReporteLabel(reporte)}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Tipo vehículo</p>
                <p className="font-medium text-gray-800">{formatValue(vehiculoTipoDesc)}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Cliente</p>
                <p className="font-medium text-gray-800">{formatValue(solicitud?.CLIENTE_NOMBRE)}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Ruta</p>
                <p className="font-medium text-gray-800">{getRutaLabel(solicitud)}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Contrato</p>
                <p className="font-medium text-gray-800">{getContratoLabel(contratoNumero, contratoId)}</p>
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 laptop:grid-cols-3">
            <div className="laptop:col-span-2">
              <p className="text-xs font-medium text-gray-600">Tarifa</p>
              <Select
                options={tarifaOptions}
                value={tarifaOptions.find((option) => String(option.value) === String(form.TARIFA_ID)) || null}
                onChange={handleTarifaChange}
                placeholder="Seleccione..."
                formatOptionLabel={formatTarifaOption}
                isLoading={loadingTarifas}
                isDisabled={loadingTarifas}
                loadingMessage={() => 'Cargando tarifas...'}
                noOptionsMessage={() =>
                  !vehiculoTipoId || !contratoId ? 'No hay contrato o tipo vehículo' : 'Sin tarifas'
                }
                menuPortalTarget={document.body}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: '38px',
                    borderColor: errors.TARIFA_ID ? '#ef4444' : '#d1d5db',
                    boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                    '&:hover': {
                      borderColor: state.isFocused ? '#3b82f6' : errors.TARIFA_ID ? '#ef4444' : '#9ca3af',
                    },
                    fontSize: '0.875rem',
                  }),
                  singleValue: (base) => ({
                    ...base,
                    fontSize: '0.875rem',
                    lineHeight: 1.2,
                  }),
                  placeholder: (base) => ({
                    ...base,
                    fontSize: '0.875rem',
                    lineHeight: 1.2,
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    padding: '0 0.75rem',
                  }),
                  input: (base) => ({
                    ...base,
                    margin: 0,
                    padding: 0,
                  }),
                  menuList: (base) => ({
                    ...base,
                    paddingTop: 4,
                    paddingBottom: 4,
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 60,
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 60,
                  }),
                  option: (base) => ({
                    ...base,
                    whiteSpace: 'normal',
                    fontSize: '0.8rem',
                    lineHeight: 1.2,
                  }),
                }}
              />
              {loadingTarifas && (
                <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-500">
                  <span className="inline-flex h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-gray-500" />
                  Cargando tarifas...
                </div>
              )}
            </div>
            <div>
              <p className="text-xs font-medium text-gray-600">Valor</p>
              <input
                type="text"
                inputMode="decimal"
                value={formattedValor}
                onChange={handleValorChange}
                readOnly={!canEditValor}
                className={`${inputBase} ${errors.SOLICITUD_DETALLE_VALOR ? inputError : inputOk} ${!canEditValor ? 'bg-gray-100' : ''}`}
              />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-600">Cantidad</p>
              <input
                type="number"
                value={form.SOLICITUD_DETALLE_CANTIDAD}
                onChange={handleCantidadChange}
                className={`${inputBase} ${errors.SOLICITUD_DETALLE_CANTIDAD ? inputError : inputOk}`}
              />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-600">Total</p>
              <input
                type="text"
                inputMode="decimal"
                value={formattedTotal}
                readOnly
                className={`${inputBase} ${inputOk} bg-gray-100`}
              />
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={handleAdd}
                disabled={isSubmitting}
                className="w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60">
                {isSubmitting ? 'Agregando...' : 'Agregar'}
              </button>
            </div>
          </div>

          <div className="mt-6 overflow-auto rounded border border-gray-200">
            <table className="w-full table-auto text-sm">
              <thead className="bg-gray-100 text-xs uppercase text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left">Item</th>
                  <th className="px-3 py-2 text-left">Tarifa</th>
                  <th className="px-3 py-2 text-right">Cantidad</th>
                  <th className="px-3 py-2 text-right">Valor</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-center">Acc</th>
                </tr>
              </thead>
              <tbody className="text-xs text-gray-700">
                {loadingDetalle ? (
                  <tr>
                    <td
                      className="px-3 py-4 text-center text-gray-500"
                      colSpan={6}>
                      Cargando tarifas...
                    </td>
                  </tr>
                ) : detalle.length === 0 ? (
                  <tr>
                    <td
                      className="px-3 py-4 text-center text-gray-500"
                      colSpan={6}>
                      Sin tarifas registradas.
                    </td>
                  </tr>
                ) : (
                  detalle.map((item) => (
                    <tr
                      key={item.SOLICITUD_DETALLE_ID}
                      className="border-t">
                      <td className="px-3 py-2">{item.TARIFA_ITEM}</td>
                      <td className="px-3 py-2">{`${item.TARIFA_GRUPO || ''} ${item.TARIFA_NOMBRE || ''}`}</td>
                      <td className="px-3 py-2 text-right">{item.SOLICITUD_DETALLE_CANTIDAD}</td>
                      <td className="px-3 py-2 text-right">{moneyFormat(item.SOLICITUD_DETALLE_VALOR)}</td>
                      <td className="px-3 py-2 text-right">{moneyFormat(item.SOLICITUD_DETALLE_TOTAL)}</td>
                      <td className="px-3 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => handleDelete(item)}
                          className="rounded px-2 py-1 text-xs font-medium text-red-600 hover:text-red-800">
                          Borrar
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {detalle.length > 0 && (
                <tfoot className="bg-gray-50 text-xs font-semibold text-gray-700">
                  <tr>
                    <td className="px-3 py-2 text-right" colSpan={4}>Total</td>
                    <td className="px-3 py-2 text-right">{moneyFormat(totals)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={onClose}
              className="rounded bg-gray-600 px-5 py-2 text-sm font-medium text-white hover:bg-gray-800">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalTarifasServicio;
